import type { User } from '@piccolohealth/pbs-common';
import { ChakraV3, Dot } from '@piccolohealth/ui';
import { inflection, P } from '@piccolohealth/util';
import type { Editor } from '@tiptap/react';
import React from 'react';
import { UserAvatar } from '../user/UserAvatar';
import type { HocuspocusState } from './hooks/useHocuspocusProvider';

interface Props {
  editor: Editor;
  state: HocuspocusState;
}

export const UserStatusIndicator = (props: Props) => {
  const { editor, state } = props;

  const { uniqueUsers, uniqueUsersCount } = React.useMemo(() => {
    const users: User[] = editor.storage?.collaborationCursor?.users ?? [];
    const uniqueUsersCount = P.countBy(users, (user) => user.id);
    const uniqueUsers: User[] = P.orderBy(
      P.uniqBy(users, (user) => user.id),
      (user) => user.name,
    );

    return { uniqueUsers, uniqueUsersCount };
  }, [editor.storage?.collaborationCursor?.users]);

  const statusColor = P.run(() => {
    switch (state.status) {
      case 'connecting':
      case 'reconnecting':
        return 'yellow.400';
      case 'connected':
        return 'green.400';
      case 'disconnected':
      case 'error':
      case 'outdated':
        return 'red.400';
    }
  });

  return (
    <ChakraV3.HStack>
      <Dot bg={statusColor} size={2} />
      <ChakraV3.AvatarGroup gap={-3}>
        {uniqueUsers.map((user: User) => {
          const count = uniqueUsersCount[user.id];

          if (!count) {
            return null;
          }

          const secondary =
            count > 1
              ? `${user.email} - ${count} ${inflection.inflect('tab', count)} open`
              : user.email;

          return (
            <UserAvatar
              key={user.id}
              name={user.name}
              secondary={secondary}
              picture={user.picture}
              showTooltip
              size='sm'
            />
          );
        })}
      </ChakraV3.AvatarGroup>
    </ChakraV3.HStack>
  );
};
